<!-- @format -->
<template>
    <!-- Dynamic Booking Content based on status -->
    <BookingLayout
        v-if="hotel"
        :class="bookingStatusClass">
        <template #status>
            <StatusChip :status="computedStatus" />
        </template>
        <template #order-number>
            <p
                v-if="getTmOrderNumber"
                class="gst-manage-booking__order-number pt-4"
                :class="{ 'tertiary--text': isStatusFailed }">
                {{
                    $t('orderNumber', {
                        orderNumber: getTmOrderNumber,
                        interpolation: { escapeValue: false },
                    })
                }}
            </p>
        </template>
        <template #default>
            <component :is="contentStatusComponent">
                <template
                    v-if="isStatusConfirmed"
                    #payment-summary>
                    <PaymentSummary
                        :currency="booking.currency"
                        :payment-items="bookingPaymentItems"
                        :fees="fees"
                        :has-hotel-reservation-only="true"
                        :total-included-fee-amount="bookingTotalIncludedFeeAmount"
                        :total-pay-later-amount="computedTotalPayLaterAmount"
                        :total-base-price="bookingTotalBasePrice"
                        :total-price="bookingTotalPrice"
                        :show-expandable-details="!isPastBooking"
                        :fees-hotels-not-included-enum="feesHotelsNotIncludedEnum"
                        class="mt-4 pa-4 gst-senary-border" />
                </template>
                <template
                    v-if="isStatusConfirmed && cancellationPolicies"
                    #cancellation-policy>
                    <CancellationPolicies
                        :policies="cancellationPolicies"
                        :booking-status="bookingStatus"
                        class="mt-4" />
                </template>
            </component>
        </template>

        <!-- TODOMMH: Discuss with designers to try putting component in the same place on all statuses -->
        <template
            v-if="!isStatusConfirmed"
            #payment-summary>
            <PaymentSummary
                :currency="booking.currency"
                :payment-items="bookingPaymentItems"
                :fees="fees"
                :has-hotel-reservation-only="true"
                :total-included-fee-amount="bookingTotalIncludedFeeAmount"
                :total-pay-later-amount="bookingTotalPayLaterAmount"
                :total-base-price="bookingTotalBasePrice"
                :total-price="bookingTotalPrice"
                :fees-hotels-not-included-enum="feesHotelsNotIncludedEnum"
                class="mt-8 pa-4 gst-senary-border" />
        </template>
    </BookingLayout>
</template>

<script>
import { mapGetters } from 'vuex';
import { ORDER_STATUS } from '@tenant/app/utils/constants/orderStatus';
import StatusChip from '@tenant/app/modules/hotel/components/chips/StatusChip.vue';
import FeesContainerMixin from '@tenant/app/views/cart/TheCheckout/Mixins/FeesContainerMixin';
import PaymentSummary from '@tenant/app/modules/order/components/PaymentSummary.vue';
import BookingLayout from './BookingLayout.vue';
import BookingContentStatusConfirmed from './BookingContentStatusConfirmed.vue';
import BookingContentStatusFailed from './BookingContentStatusFailed.vue';
import BookingContentStatusRefunded from './BookingContentStatusRefunded.vue';
import BookingContentStatusCancelled from './BookingContentStatusCancelled.vue';
import BookingContentStatusPending from './BookingContentStatusPending.vue';
import StatusMixin from '../mixins/StatusMixin';
import CancellationPolicies from '../_components/CancellationPolicies.vue';

export default {
    name: 'BookingContent',
    components: {
        BookingLayout,
        /* eslint-disable vue/no-unused-components */
        BookingContentStatusConfirmed,
        BookingContentStatusFailed,
        BookingContentStatusPending,
        BookingContentStatusRefunded,
        BookingContentStatusCancelled,
        /* eslint-enable vue/no-unused-components */
        StatusChip,
        CancellationPolicies,
        PaymentSummary,
    },
    mixins: [FeesContainerMixin, StatusMixin],
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'modules.hotel.features.manageBooking.theManageBooking',
    },
    computed: {
        ...mapGetters({
            booking: 'booking/get',
            hotel: 'booking/getHotel',
            getOrderPartnerId: 'booking/getOrderPartnerId',
            getTmOrderNumber: 'booking/getTmOrderNumber',
            cancellationPolicies: 'booking/getCancellationPolicies',
        }),
        contentStatusComponent() {
            switch (this.bookingStatus) {
                case ORDER_STATUS.RESERVED.value:
                    return BookingContentStatusConfirmed;
                case ORDER_STATUS.FAILED.value:
                    return this.isOrderRefunded
                        ? BookingContentStatusRefunded
                        : BookingContentStatusFailed;
                case ORDER_STATUS.DELETED.value:
                    return BookingContentStatusCancelled;
                default:
                    return BookingContentStatusPending;
            }
        },
        currency() {
            return this.booking.currency;
        },
        // In order to not change FeesContainerMixin.js we need to have a computed named "order" to comply with what is used in mixin
        order() {
            return this.booking;
        },
        bookingStatusClass() {
            return this.bookingStatus && `gst-booking-${this.bookingStatus.toLocaleLowerCase()}`;
        },
        bookingTotalPrice() {
            return this.booking.totalPrice;
        },
        bookingTotalBasePrice() {
            return this.booking.basePrice;
        },
        bookingTotalIncludedFeeAmount() {
            return this.booking.totalIncludedFeeAmount;
        },
        bookingTotalPayLaterAmount() {
            return this.isStatusConfirmed ? this.booking.totalPayLaterAmount : 0;
        },
        bookingPaymentItems() {
            let ret = [];
            const { hotel } = this;

            ret.push({
                name: hotel.hotel.room.name?.toLowerCase(),
                quantity: hotel.accommodation.roomsCount,
            });

            return ret;
        },
        computedStatus() {
            const bookingStatusOrPastBooking = this.isPastBooking
                ? ORDER_STATUS.PAST_BOOKING.value
                : this.bookingStatus;
            return !this.isStatusRefunded ? bookingStatusOrPastBooking : this.orderStatus;
        },
        computedTotalPayLaterAmount() {
            return !this.isPastBooking ? this.bookingTotalPayLaterAmount : null;
        },
    },
};
</script>
<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-booking-confirmed {
    gap: theme-spacing(4);

    @include mobile-only {
        flex-direction: column;
    }
}
</style>
